import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  MapPin, 
  Calendar, 
  Users, 
  DollarSign,
  Search,
  List,
  Map as MapIcon,
  ChevronRight,
  Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];

// US States with approximate center coordinates
const US_REGIONS = {
  'TX': { name: 'Texas', lat: 31.0, lng: -100.0 },
  'CA': { name: 'California', lat: 36.7, lng: -119.4 },
  'FL': { name: 'Florida', lat: 27.6, lng: -81.5 },
  'NY': { name: 'New York', lat: 43.0, lng: -75.0 },
  'IL': { name: 'Illinois', lat: 40.0, lng: -89.0 },
  'PA': { name: 'Pennsylvania', lat: 41.2, lng: -77.2 },
  'OH': { name: 'Ohio', lat: 40.4, lng: -82.9 },
  'GA': { name: 'Georgia', lat: 32.2, lng: -83.4 },
  'NC': { name: 'North Carolina', lat: 35.8, lng: -79.0 },
  'MI': { name: 'Michigan', lat: 44.3, lng: -85.6 },
  'AZ': { name: 'Arizona', lat: 34.0, lng: -111.1 },
  'WA': { name: 'Washington', lat: 47.4, lng: -120.7 },
  'CO': { name: 'Colorado', lat: 39.0, lng: -105.5 },
};

const LEVEL_COLORS = {
  district: '#3B82F6',
  regional: '#22C55E',
  state: '#A855F7',
  national: '#F97316'
};

export const TournamentMap = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState('');
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [viewMode, setViewMode] = useState('map');
  const [selectedRegion, setSelectedRegion] = useState(null);

  useEffect(() => {
    fetchTournaments();
  }, [ageFilter, genderFilter]);

  const fetchTournaments = async () => {
    try {
      const params = new URLSearchParams();
      if (ageFilter !== 'All') params.append('age_category', ageFilter);
      if (genderFilter !== 'All') params.append('gender', genderFilter);
      params.append('status', 'upcoming');
      
      const response = await axios.get(`${API}/tournaments?${params}`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group tournaments by state
  const groupedByState = tournaments.reduce((acc, t) => {
    const location = t.location || '';
    const statePart = location.split(',').pop()?.trim().toUpperCase() || 'OTHER';
    const stateCode = statePart.length === 2 ? statePart : 'OTHER';
    
    if (!acc[stateCode]) {
      acc[stateCode] = [];
    }
    acc[stateCode].push(t);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredTournaments = searchLocation
    ? tournaments.filter(t => 
        t.location?.toLowerCase().includes(searchLocation.toLowerCase())
      )
    : tournaments;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournament-map-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">TOURNAMENT MAP</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Find tournaments by location across the country
          </p>
        </div>
        
        {/* View Toggle */}
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('map')}
            className={viewMode === 'map' ? 'btn-primary' : ''}
            data-testid="map-view-btn"
          >
            <MapIcon className="w-4 h-4 mr-1" />
            Map
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'btn-primary' : ''}
            data-testid="list-view-btn"
          >
            <List className="w-4 h-4 mr-1" />
            List
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by city or state..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="pl-10"
                data-testid="location-search"
              />
            </div>
            
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="age-filter">
                <SelectValue placeholder="Age" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="gender-filter">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6">
        {Object.entries(LEVEL_COLORS).map(([level, color]) => (
          <div key={level} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-sm capitalize">{level}</span>
          </div>
        ))}
      </div>

      {viewMode === 'map' ? (
        /* Map View */
        <Card className="overflow-hidden" data-testid="map-container">
          <CardContent className="p-0">
            <div className="relative bg-muted/30 min-h-[500px]">
              {/* Simple US Map representation */}
              <div className="absolute inset-0 p-8">
                <div className="relative w-full h-full">
                  {/* Map Background */}
                  <div className="absolute inset-0 border-2 border-dashed border-muted-foreground/20 rounded-xl flex items-center justify-center">
                    <span className="text-muted-foreground/30 text-lg">US Tournament Map</span>
                  </div>
                  
                  {/* State Markers */}
                  {Object.entries(US_REGIONS).map(([code, region]) => {
                    const stateTournaments = groupedByState[code] || [];
                    if (stateTournaments.length === 0) return null;
                    
                    // Position calculation (simplified)
                    const left = `${((region.lng + 125) / 60) * 100}%`;
                    const top = `${((50 - region.lat) / 30) * 100}%`;
                    
                    return (
                      <div
                        key={code}
                        className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                        style={{ left, top }}
                        onClick={() => setSelectedRegion(selectedRegion === code ? null : code)}
                        data-testid={`marker-${code}`}
                      >
                        <div className={`relative ${selectedRegion === code ? 'scale-125' : ''} transition-transform`}>
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                            style={{ backgroundColor: LEVEL_COLORS[stateTournaments[0]?.level || 'district'] }}
                          >
                            {stateTournaments.length}
                          </div>
                          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap">
                            {code}
                          </div>
                        </div>
                        
                        {/* Hover tooltip */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[200px]">
                            <p className="font-bold">{region.name}</p>
                            <p className="text-sm text-muted-foreground">{stateTournaments.length} tournament(s)</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Selected Region Details */}
            {selectedRegion && groupedByState[selectedRegion] && (
              <div className="border-t p-4 bg-muted/30" data-testid="region-details">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  Tournaments in {US_REGIONS[selectedRegion]?.name || selectedRegion}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedByState[selectedRegion].map(tournament => (
                    <Card key={tournament.id} className="card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <Badge 
                            variant="outline"
                            style={{ 
                              backgroundColor: `${LEVEL_COLORS[tournament.level]}20`,
                              color: LEVEL_COLORS[tournament.level],
                              borderColor: `${LEVEL_COLORS[tournament.level]}40`
                            }}
                          >
                            {tournament.level}
                          </Badge>
                          <Badge variant="secondary">{tournament.age_category}</Badge>
                        </div>
                        <h4 className="font-bold line-clamp-1">{tournament.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{tournament.location}</p>
                        <div className="flex items-center justify-between mt-3 text-sm">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(tournament.start_date)}
                          </span>
                          <Link to={`/tournaments/${tournament.id}`}>
                            <Button size="sm" variant="ghost" className="h-7 px-2">
                              View <ChevronRight className="w-3 h-3" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* List View */
        <div className="space-y-4">
          {filteredTournaments.length > 0 ? (
            filteredTournaments.map((tournament, index) => (
              <Card key={tournament.id} className="card-hover" data-testid={`list-item-${index}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${LEVEL_COLORS[tournament.level]}20` }}
                    >
                      <MapPin className="w-6 h-6" style={{ color: LEVEL_COLORS[tournament.level] }} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold">{tournament.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {tournament.level}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{tournament.location}</p>
                    </div>
                    
                    <div className="text-right hidden md:block">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Date</p>
                          <p className="font-medium">{formatDate(tournament.start_date)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Teams</p>
                          <p className="font-medium">{tournament.registered_teams}/{tournament.max_teams}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Entry</p>
                          <p className="font-medium">${tournament.entry_fee}</p>
                        </div>
                      </div>
                    </div>
                    
                    <Link to={`/tournaments/${tournament.id}`}>
                      <Button variant="outline" size="sm">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-xl font-bold mb-2">No Tournaments Found</h3>
                <p className="text-muted-foreground">Try adjusting your filters</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
